import { useAccountModelStore } from '../store/accountModelStore';
import { fetchModelJsonApi, flyoutLinkMappings } from './constants';
import { addSubDirectoryPrefix } from './helper';

export function getValueFromHref<T extends { modelKey: string; activeKey: string }>(
  href: string,
  map: Record<string, T>
): T | null {
  // Remove query parameters
  const cleanHref: string = href.split('?')[0];

  // Remove .mi or .html or other suffixes
  const basePath = cleanHref.replace(/(\.mi|\.html)$/i, '');

  // Try direct match
  if (map[basePath]) return map[basePath];

  // Try match without trailing slash
  const noSlash = basePath.replace(/\/$/, '');
  if (map[noSlash]) return map[noSlash];

  // Try match with only the last segment
  const lastSegment = '/' + basePath.split('/').pop();
  if (map[lastSegment]) return map[lastSegment];

  // Try includes match for keys containing basePath
  const includesKey = Object.keys(map).find(k => k.includes(basePath));
  if (includesKey) return map[includesKey];

  return null;
}

/**
 * Fetches model JSON data for a given link using the model API endpoint.
 * Returns the parsed JSON response or null on error.
 */
export async function fetchLinkJson(link: string, additionalHeaders?: { [key: string]: string }) {
  try {
    const apiUrl = addSubDirectoryPrefix(`${fetchModelJsonApi}?model=${link}`);
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...additionalHeaders,
      },
    });
    return await response.json();
  } catch (err) {
    console.error('Error in fetching model-json:', err);
    return null;
  }
}

export const useFlyoutNavigation = (
  updatePageErrors?: (args: { text: string; class: string; type: string }) => void,
  errorText?: string
) => {
  const { modelData, setModelData, setModelKey, setActiveKey } = useAccountModelStore(state => state);
  const handleFlyoutNavigation = async (linkKey: string) => {
    const mapping = getValueFromHref(linkKey, flyoutLinkMappings);
    if (!mapping) return;

    //If data already exists, skip fetch
    if (modelData?.[mapping.modelKey]) {
      setActiveKey(mapping.activeKey);
      setModelKey(mapping.modelKey);
      return;
    }

    try {
      //Fetch only if not present
      const fetchedData = await fetchLinkJson(mapping.modelKey);
      if (fetchedData?.data) {
        // Merge new data into existing modelData
        setModelData({
          ...modelData,
          [mapping.modelKey]: fetchedData,
        });
        setModelKey(mapping.modelKey);
        setActiveKey(mapping.activeKey);
      } else {
        // Data not found
        updatePageErrors?.({
          text: errorText ?? '',
          class: 'error-sev1',
          type: 'alert',
        });
      }
    } catch (error) {
      // API call failed
      updatePageErrors?.({
        text: errorText ?? '',
        class: 'error-sev1',
        type: 'alert',
      });
    }
  };

  return { handleFlyoutNavigation };
};

export function setupLinkInterception(targetLink: string, callback: () => Promise<void> | void) {
  function preventAnchorRedirects(event: Event) {
    const target = event.target as Element;

    const link = target?.closest('a');

    if (link && link.href && link.href.includes(targetLink)) {
      event?.preventDefault();
      callback();
    }
  }

  function interceptAnchorClicks(id?: string) {
    let element: HTMLElement | Document = document;
    if (id) {
      const target = document.getElementById(id);
      if (target) {
        element = target;
      }
    }
    element.addEventListener('click', preventAnchorRedirects);

    element.addEventListener('keydown', event => {
      if ((event as KeyboardEvent).key === 'Enter') {
        preventAnchorRedirects(event);
      }
    });

    return () => {
      element.removeEventListener('click', preventAnchorRedirects);
      element.removeEventListener('keydown', preventAnchorRedirects);
    };
  }
  return { interceptAnchorClicks };
}
